/**
 * Dose table model for AI Dosimetry workflow.
 *
 * All dose values from the API are computed at a reference injected activity of 2 GBq.
 * The user can adjust the injected activity or individual Gy values; all other values
 * are linearly rescaled to maintain proportionality.
 */

/**
 * A single entry in the dose table.
 */
export interface IDoseEntry {
    readonly key: string;
    readonly label: string;
    readonly unit: string;
    readonly value: number;
}

/**
 * Complete dose table with all entries and the reference activity.
 */
export interface IDoseTable {
    readonly referenceActivityGBq: number;
    readonly entries: readonly IDoseEntry[];
}

/** The reference injected activity used by the API (always 2 GBq). */
export const REFERENCE_ACTIVITY_GBQ = 2;

/**
 * Ordered keys for dose table display with their labels.
 */
export const DOSE_TABLE_KEYS: readonly { key: string; label: string }[] = [
    { key: 'InjectedActivity', label: 'Injected Activity' },
    { key: 'WholeLiver', label: 'Whole Liver' },
    { key: 'PerfusedVolume', label: 'Perfused Volume' },
    { key: 'NonPerfusedVolume', label: 'Non-Perfused Volume' },
    { key: 'TreatedTumors', label: 'Treated Tumors' },
    { key: 'NonTumoralLiver', label: 'Non-Tumoral Liver' },
    { key: 'NonTumoralPerfusedLiver', label: 'Non-Tumoral Perfused Liver' },
    { key: 'LungShunt', label: 'Lung Shunt' },
];

export const DOSE_TABLE_INJECTED_ACTIVITY_KEY = 'InjectedActivity';

export const DOSE_TABLE_SUMMARY_KEYS = DOSE_TABLE_KEYS.filter(
    (k) => k.key !== DOSE_TABLE_INJECTED_ACTIVITY_KEY
).map((k) => k.key);

/**
 * Parse raw dosimetry metadata into a structured dose table.
 *
 * @param metadata - Raw dosimetry metadata from API (`metadata.dosimetry`)
 * @returns Structured dose table
 */
export function parseDoseTable(
    metadata: Readonly<Record<string, { unit: string; value: number }>>
): IDoseTable {
    const entries: IDoseEntry[] = DOSE_TABLE_KEYS.map(({ key, label }) => {
        const raw = metadata[key];
        return {
            key,
            label,
            unit: raw?.unit ?? (key === DOSE_TABLE_INJECTED_ACTIVITY_KEY ? 'GBq' : 'Gy'),
            value: raw?.value ?? 0,
        };
    });

    return {
        referenceActivityGBq: REFERENCE_ACTIVITY_GBQ,
        entries,
    };
}

/**
 * Scale the entire dose table to a new injected activity.
 *
 * All Gy values are linearly proportional to injected activity.
 * `newGy = referenceGy * (newActivity / referenceActivity)`
 *
 * @param table - Original dose table (at reference activity)
 * @param newActivityGBq - New injected activity in GBq
 * @returns New dose table with all values scaled
 */
export function scaleDoseTableByActivity(table: IDoseTable, newActivityGBq: number): IDoseTable {
    const scale = newActivityGBq / table.referenceActivityGBq;

    return {
        referenceActivityGBq: table.referenceActivityGBq,
        entries: table.entries.map((entry) => {
            if (entry.key === DOSE_TABLE_INJECTED_ACTIVITY_KEY) {
                return { ...entry, value: newActivityGBq };
            }
            return { ...entry, value: entry.value * scale };
        }),
    };
}

/**
 * Scale the dose table by changing one specific Gy entry.
 *
 * Back-computes the required injected activity from the desired value,
 * then scales all other entries proportionally.
 *
 * @param table - Original dose table (at reference activity)
 * @param entryKey - The key of the entry being changed (must not be 'InjectedActivity')
 * @param newValue - The new desired Gy value for that entry
 * @returns New dose table with all values scaled, or the original table if the entry's reference value is 0
 */
export function scaleDoseTableByEntry(table: IDoseTable, entryKey: string, newValue: number): IDoseTable {
    const referenceEntry = table.entries.find((e) => e.key === entryKey);
    if (!referenceEntry || referenceEntry.value === 0) {
        return table;
    }

    // Back-compute: newActivity = referenceActivity * (newValue / referenceValue)
    const newActivityGBq = table.referenceActivityGBq * (newValue / referenceEntry.value);

    return scaleDoseTableByActivity(table, newActivityGBq);
}
